import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import type { WorkingMapProps, DealerAssignmentSummary } from '../types/map.types';
import { getDealerProgress } from '../utils/dealerProgress';
import { getStopLocationKey, isCustomerStop } from '@/lib/manualStops/locationKey';
import { CustomerStopMarker } from './CustomerStopMarker';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  dealerLocationsSignature
} from '../utils/mapDefaults';
import { useCenterOnceWhenKnown } from '../hooks/useCenterOnceWhenKnown';

// Add custom styles for marker hover effects with golden glow and loading animation
const markerStyles = `
  .custom-marker-icon > div {
    transition: all 0.2s ease;
  }
  .custom-marker-icon:hover > div {
    transform: scale(1.1);
    opacity: 1 !important;
    background-color: #5C4521 !important;
    box-shadow: 0 0 16px rgba(245, 230, 211, 0.6), 0 3px 6px rgba(0,0,0,0.4) !important;
  }
  .manual-stop-marker {
    z-index: 1000 !important;
  }
  .manual-stop-marker:hover > div {
    transform: scale(1.1);
    opacity: 1 !important;
    background-color: #0284c7 !important;
    box-shadow: 0 0 16px rgba(14, 165, 233, 0.6), 0 3px 6px rgba(0,0,0,0.4) !important;
  }
  .clustered-marker > div {
    border: 2px solid #3B82F6 !important;
    background: linear-gradient(45deg, #3B82F6, #1D4ED8) !important;
  }
  .clustered-marker:hover > div {
    background: linear-gradient(45deg, #1D4ED8, #1E40AF) !important;
    box-shadow: 0 0 16px rgba(59, 130, 246, 0.6), 0 3px 6px rgba(0,0,0,0.4) !important;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.8); }
    to { opacity: 1; transform: scale(1); }
  }
  .leaflet-marker-icon {
    animation: fadeIn 0.3s ease-out;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('marker-hover-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'marker-hover-styles';
    style.textContent = markerStyles;
    document.head.appendChild(style);
  }
}

// Fix for default markers in React-Leaflet
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)['_getIconUrl'];
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Types are now imported from map.types.ts for consistency

// Bubble size scales with recall volume (the primary marker signal).
const getDealerMarkerSize = (recallCount: number): number => {
  if (recallCount === 1) return 25;
  if (recallCount <= 5) return 30;
  if (recallCount <= 10) return 35;
  if (recallCount <= 20) return 40;
  return 45;
};

// Bubble opacity hints at density (light → critical).
const getDensityOpacity = (recallCount: number): number => {
  if (recallCount === 1) return 0.7;
  if (recallCount <= 5) return 0.8;
  if (recallCount <= 15) return 0.9;
  return 1.0;
};

interface DealerMarkerVisualOptions {
  colorByTechnician: boolean;
  selectedTechnicianIds: string[];
  getTechnicianColor?: ((technicianId: string) => string) | undefined;
}

interface DealerMarkerVisuals {
  pctHandled: number;
  fullyHandled: boolean;
  ringColor: string;
  /** Non-matching dealer while a technician filter is active → faint outline */
  dimmed: boolean;
  /** Matching dealer while a technician filter is active → pops in tech color */
  emphasized: boolean;
}

const DEFAULT_RING_COLOR = '#10b981';

// Order-sensitive string array equality (used by the memo comparator).
const sameOrderStringArray = (a: string[] = [], b: string[] = []): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

// Pick the dominant technician id from a count map, restricted to `onlyIds`
// when provided. Returns undefined when there's nothing to pick.
const dominantTechnician = (
  byTechnician: Map<string, number>,
  onlyIds?: string[]
): string | undefined => {
  let best = -1;
  let winner: string | undefined;
  byTechnician.forEach((count, techId) => {
    if (onlyIds && !onlyIds.includes(techId)) return;
    if (count > best) {
      best = count;
      winner = techId;
    }
  });
  return winner;
};

// Roll up manual stops into the same `technician_id -> count` shape that dealer
// summaries use, so manual stops can flow through getDealerMarkerVisuals
// unchanged (highlight/dim/color-by-tech all key off this map).
const manualStopsByTechnician = (stops: Array<{ technicianId?: string }>): Map<string, number> => {
  const m = new Map<string, number>();
  stops.forEach(s => {
    if (s.technicianId) m.set(s.technicianId, (m.get(s.technicianId) || 0) + 1);
  });
  return m;
};

// Combine two technician count maps (recalls + manual stops at the same dealer).
const mergeTechCounts = (a: Map<string, number>, b: Map<string, number>): Map<string, number> => {
  const merged = new Map(a);
  b.forEach((count, techId) => merged.set(techId, (merged.get(techId) || 0) + count));
  return merged;
};

// Decide a marker's ring color: matched dealers read by their selected
// technician's color (so coverage areas are obvious even when the global
// color-by-tech toggle is off); otherwise the optional color-by-tech default.
const resolveRingColor = (
  summary: DealerAssignmentSummary,
  options: DealerMarkerVisualOptions,
  techActive: boolean,
  matchesTech: boolean
): string => {
  const { colorByTechnician, selectedTechnicianIds, getTechnicianColor } = options;
  if (!getTechnicianColor) return DEFAULT_RING_COLOR;
  if (techActive && matchesTech) {
    const domId = dominantTechnician(summary.byTechnician, selectedTechnicianIds);
    return domId ? getTechnicianColor(domId) : DEFAULT_RING_COLOR;
  }
  if (colorByTechnician && summary.byTechnician.size > 0) {
    const domId = dominantTechnician(summary.byTechnician);
    return domId ? getTechnicianColor(domId) : DEFAULT_RING_COLOR;
  }
  return DEFAULT_RING_COLOR;
};

// Derive the assignment encoding (ring %, done-state, color, emphasis) for one
// dealer from its rollup. Kept pure + module-level to keep the render callback
// simple and within complexity limits.
const getDealerMarkerVisuals = (
  summary: DealerAssignmentSummary | undefined,
  options: DealerMarkerVisualOptions
): DealerMarkerVisuals => {
  // % is over ACTIONABLE work only: handled / (handled + workable-unassigned).
  // Advance-notice / paused recalls are excluded from `unassigned` upstream.
  // Shared with the right-panel dealer cards via getDealerProgress.
  const { pctHandled, fullyHandled } = getDealerProgress(summary);

  const techActive = options.selectedTechnicianIds.length > 0;
  const matchesTech = !!(
    summary && options.selectedTechnicianIds.some(id => summary.byTechnician.has(id))
  );

  const ringColor = summary
    ? resolveRingColor(summary, options, techActive, matchesTech)
    : DEFAULT_RING_COLOR;

  return {
    pctHandled,
    fullyHandled,
    ringColor,
    dimmed: techActive && !matchesTech,
    emphasized: techActive && matchesTech
  };
};

/**
 * WorkingMap - Leaflet-based map component for displaying dealer locations
 *
 * IMPORTANT: Map Position Behavior
 * -------------------------------
 * This component maintains its position even when dealerLocations becomes empty.
 * The map will NOT re-center to Florida when dragging to areas without recalls.
 *
 * Key Features:
 * - Memoized to prevent unnecessary re-renders
 * - Handles empty dealer locations gracefully
 * - Debounces viewport changes to prevent excessive data loading
 *
 * DO NOT add logic that would cause the map to re-center based on data!
 * The center prop is only used for initial render.
 *
 * @component
 */
/* eslint-disable max-lines-per-function, complexity */
const WorkingMap: React.FC<WorkingMapProps> = React.memo(
  ({
    dealerLocations,
    onMapBoundsChange,
    onDealerClick,
    initialCenter,
    initialZoom,
    flyToTarget,
    stagedManualStops = [],
    onManualStopClick,
    selectedTechnicianIds = [],
    colorByTechnician = false,
    getTechnicianColor
  }) => {
    const [map, setMap] = useState<L.Map | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    // Cache manual-stop DivIcons by (count, hasBlueRing) so identical markers
    // reuse one Leaflet icon instance — prevents DOM churn and fadeIn replay
    const manualStopIconCacheRef = useRef<Map<string, L.DivIcon>>(new Map());
    // Cache dealer DivIcons by their full visual signature for the same reason
    const dealerIconCacheRef = useRef<Map<string, L.DivIcon>>(new Map());

    useEffect(() => {
      if (map) {
        // Force a resize to ensure tiles load
        const resizeTimer = setTimeout(() => {
          // Check if map still exists and has a container before calling invalidateSize
          if (map && map.getContainer()) {
            try {
              map.invalidateSize();
            } catch {
              // Map may have unmounted, silently handle
            }
          }
        }, 100);

        // Cleanup timeout on unmount
        return () => clearTimeout(resizeTimer);
      }
      return undefined;
    }, [map]);

    // Use tenant-configured center if provided, otherwise default to Florida
    const center: [number, number] = initialCenter || DEFAULT_MAP_CENTER;
    const zoom = initialZoom ?? DEFAULT_MAP_ZOOM;
    // `center` above is read once at mount, often before the tenant has loaded.
    useCenterOnceWhenKnown(map, initialCenter, zoom);

    // Add a unique key to track component lifecycle and cleanup
    useEffect(() => {
      return () => {
        // Clean up map reference on unmount
        if (mapRef.current) {
          try {
            mapRef.current.off(); // Remove all event listeners
            mapRef.current = null;
          } catch {
            // Silently handle cleanup errors
          }
        }
      };
    }, []);

    // Fly to target location (e.g., VIN search result)
    useEffect(() => {
      if (!flyToTarget || !mapRef.current) return;
      try {
        mapRef.current.flyTo([flyToTarget.lat, flyToTarget.lng], flyToTarget.zoom, {
          duration: 1.5
        });
      } catch {
        // Silently handle if map is not ready
      }
    }, [flyToTarget]);

    // Create custom icon for individual dealers.
    //
    // Visual channels:
    //   - bubble SIZE      = recall volume (primary signal, unchanged)
    //   - progress RING    = share of recalls already handled (route/tech/staged)
    //   - desaturate+check = every recall handled ("skip this one")
    //   - blue badge       = manual stops (unchanged)
    //   - dimmed           = doesn't match the active technician filter
    //
    // `pctHandled` is bucketed (0/25/50/75/100) so the icon cache stays small.
    const createDealerIcon = (
      recallCount: number,
      manualStopCount: number = 0,
      opts: {
        pctHandled?: number;
        fullyHandled?: boolean;
        ringColor?: string;
        dimmed?: boolean;
        emphasized?: boolean;
      } = {}
    ) => {
      const {
        fullyHandled = false,
        ringColor = '#10b981',
        dimmed = false,
        emphasized = false
      } = opts;
      const pctRaw = Math.max(0, Math.min(100, opts.pctHandled ?? 0));
      // Bucket to keep the cache small while staying visually faithful
      const pctBucket = Math.round(pctRaw / 25) * 25;

      const size = getDealerMarkerSize(recallCount);

      const cacheKey =
        `${recallCount}|${size}|${manualStopCount}|${pctBucket}` +
        `|${fullyHandled}|${ringColor}|${dimmed}|${emphasized}`;
      const cached = dealerIconCacheRef.current.get(cacheKey);
      if (cached) return cached;

      // De-emphasized (a technician filter is active and this dealer has none of
      // their work): render a faint hollow outline for context — no ring/badges.
      if (dimmed) {
        const ghostSize = Math.max(18, Math.round(size * 0.7));
        const ghostHtml = `<div style="
            width: ${ghostSize}px; height: ${ghostSize}px; border-radius: 50%;
            background: rgba(255,255,255,0.55); border: 1px solid hsl(39 16% 70%);
            color: hsl(39 16% 45%); opacity: 0.6;
            display: flex; align-items: center; justify-content: center;
            font-size: 10px; font-weight: 600; font-family: Arial, sans-serif;">
            ${recallCount}
          </div>`;
        const ghostIcon = L.divIcon({
          html: ghostHtml,
          className: 'custom-marker-icon',
          iconSize: L.point(ghostSize, ghostSize, true)
        });
        dealerIconCacheRef.current.set(cacheKey, ghostIcon);
        return ghostIcon;
      }

      // Use consistent dealer colors
      const baseColor = '#7C5F2C'; // Bronze from Vehix
      const darkColor = '#5C4521';
      const opacity = getDensityOpacity(recallCount);
      const backgroundColor = recallCount > 15 ? darkColor : baseColor;
      const textColor = '#FFFFFF';

      // A ring is drawn for partial progress and stays as a solid ring once the
      // dealer is fully handled, so the technician color persists through
      // completion. The desaturate + check (applied to the inner bubble only)
      // still reads as "done"; zero-progress keeps today's plain look.
      const showRing = fullyHandled || pctBucket > 0;
      const ringWidth = size <= 30 ? 3 : 4;

      // Manual stop badge (blue circle with count, top-right)
      const badgeHtml =
        manualStopCount > 0
          ? `<div style="position: absolute; top: -6px; right: -6px;
             background-color: #0ea5e9; color: white;
             border-radius: 50%; width: 18px; height: 18px;
             display: flex; align-items: center; justify-content: center;
             font-size: 10px; font-weight: bold; border: 2px solid white;
             box-shadow: 0 1px 3px rgba(0,0,0,0.3); z-index: 2;">
             ${manualStopCount}
           </div>`
          : '';

      // Done check badge (green, bottom-right) — mirrors the blue badge corner
      const checkHtml = fullyHandled
        ? `<div style="position: absolute; bottom: -5px; right: -5px;
             background-color: #10b981; color: white;
             border-radius: 50%; width: 16px; height: 16px;
             display: flex; align-items: center; justify-content: center;
             font-size: 10px; font-weight: bold; line-height: 1;
             border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3); z-index: 2;">
             ✓
           </div>`
        : '';

      const bubbleFilter = fullyHandled ? 'filter: grayscale(0.6);' : '';
      const bubbleOpacity = fullyHandled ? 0.85 : opacity;
      // Matched dealer in a technician filter: pop with a tech-colored border.
      const bubbleBorder = emphasized ? `3px solid ${ringColor}` : '2px solid white';
      const bubbleShadow = emphasized
        ? '0 0 0 1px rgba(255,255,255,0.9), 0 3px 6px rgba(0,0,0,0.35)'
        : '0 2px 4px rgba(0,0,0,0.3)';

      const bubbleHtml = `<div style="background-color: ${backgroundColor}; color: ${textColor};
          border-radius: 50%;
          width: ${size}px; height: ${size}px;
          display: flex; align-items: center; justify-content: center;
          font-weight: bold; border: ${bubbleBorder};
          box-shadow: ${bubbleShadow};
          font-size: 12px; font-family: Arial, sans-serif; opacity: ${bubbleOpacity};
          ${bubbleFilter}
          transition: all 0.2s ease;">
          ${recallCount}
        </div>`;

      // When showing a ring, wrap the bubble in a slightly larger circle whose
      // conic-gradient background reads as a progress arc around the bubble.
      // Fully-handled dealers fill the whole ring (solid tech color) so coverage
      // stays visible at a glance even after every recall is assigned.
      const ringPctBucket = fullyHandled ? 100 : pctBucket;
      const ringGradient =
        `conic-gradient(${ringColor} 0 ${ringPctBucket}%, ` +
        `rgba(124,95,44,0.25) ${ringPctBucket}% 100%)`;
      const innerHtml = showRing
        ? `<div style="border-radius: 50%; padding: ${ringWidth}px; box-sizing: content-box;
             background: ${ringGradient};
             box-shadow: 0 2px 4px rgba(0,0,0,0.25); display: flex;">
             ${bubbleHtml}
           </div>`
        : bubbleHtml;

      const wrapperStyle =
        'position: relative; display: inline-flex; animation: fadeIn 0.3s ease-out;';
      const html = `<div style="${wrapperStyle}">
        ${innerHtml}
        ${badgeHtml}
        ${checkHtml}
      </div>`;

      // Account for ring + badge overflow so the marker isn't clipped/offset
      const outer = showRing ? size + ringWidth * 2 : size;
      const iconSize = manualStopCount > 0 || fullyHandled ? outer + 12 : outer;

      const icon = L.divIcon({
        html: html,
        className: 'custom-marker-icon',
        iconSize: L.point(iconSize, iconSize, true)
      });
      dealerIconCacheRef.current.set(cacheKey, icon);
      return icon;
    };

    // Create icon for manual stops (blue numbered circles like dealer markers).
    // When a technician filter / color-by-tech is active the OUTSIDE of the
    // circle takes the technician treatment (tech-colored ring when matched,
    // faded when not), mirroring dealer markers; the blue body is unchanged.
    const createManualStopIcon = (
      count: number,
      hasBlueRing: boolean,
      opts: { ringColor?: string; dimmed?: boolean; emphasized?: boolean } = {}
    ) => {
      const { ringColor = DEFAULT_RING_COLOR, dimmed = false, emphasized = false } = opts;
      const key = `${count}|${hasBlueRing}|${ringColor}|${dimmed}|${emphasized}`;
      const cached = manualStopIconCacheRef.current.get(key);
      if (cached) return cached;

      // Dynamic sizing like dealer markers
      let size;
      if (count === 1) {
        size = 25;
      } else if (count <= 5) {
        size = 30;
      } else if (count <= 10) {
        size = 35;
      } else {
        size = 40;
      }

      const color = '#0ea5e9'; // Sky blue (brand accent)
      const darkColor = '#0284c7'; // Darker blue for high counts
      const bgColor = count > 10 ? darkColor : color;

      // Border (the "outside"): a technician filter takes precedence — matched
      // stops get a tech-colored ring, unmatched stops fade back. Otherwise fall
      // back to the existing blue-ring-at-dealer / white-border behavior.
      let borderColor = hasBlueRing ? '#3B82F6' : 'white';
      let borderWidth = hasBlueRing ? 3 : 2;
      if (emphasized) {
        borderColor = ringColor;
        borderWidth = 3;
      }

      const opacity = dimmed ? 0.4 : 1;
      const boxShadow = emphasized
        ? '0 0 0 1px rgba(255,255,255,0.9), 0 3px 6px rgba(0,0,0,0.35)'
        : '0 2px 6px rgba(0,0,0,0.35)';

      const html = `<div style="background-color: ${bgColor}; color: white;
        border-radius: 50%;
        width: ${size}px; height: ${size}px;
        display: flex; align-items: center; justify-content: center;
        font-weight: bold; border: ${borderWidth}px solid ${borderColor};
        box-shadow: ${boxShadow};
        opacity: ${opacity};
        font-size: 12px; font-family: Arial, sans-serif;
        transition: all 0.2s ease;
        animation: fadeIn 0.3s ease-out;">
        ${count}
        </div>`;

      const icon = L.divIcon({
        html: html,
        className: 'manual-stop-marker',
        iconSize: L.point(size, size, true)
      });
      manualStopIconCacheRef.current.set(key, icon);
      return icon;
    };

    // Customer stops: same sky-blue family as manual stops, but a rounded SQUARE with a
    // house glyph — shape is the only visual channel not already used (fill = category,
    // size = count, ring = technician). One household per marker, so size is fixed; a
    // household with several stops shows a small count badge. `fanIndex` nudges markers
    // that share a point (units in one building) so each stays clickable.
    const createCustomerStopIcon = (
      count: number,
      fanIndex: number,
      opts: { ringColor?: string; dimmed?: boolean; emphasized?: boolean } = {}
    ) => {
      const { ringColor = DEFAULT_RING_COLOR, dimmed = false, emphasized = false } = opts;
      const key = `customer|${count}|${fanIndex}|${ringColor}|${dimmed}|${emphasized}`;
      const cached = manualStopIconCacheRef.current.get(key);
      if (cached) return cached;

      const size = 26;
      const borderColor = emphasized ? ringColor : 'white';
      const borderWidth = emphasized ? 3 : 2;
      const boxShadow = emphasized
        ? '0 0 0 1px rgba(255,255,255,0.9), 0 3px 6px rgba(0,0,0,0.35)'
        : '0 2px 6px rgba(0,0,0,0.35)';
      // Static SVG (lucide "house") — no user data is ever interpolated into this HTML.
      const houseSvg =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" ' +
        'fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 ' +
        '0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 ' +
        '0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>';
      const badge =
        count > 1
          ? `<div style="position:absolute;top:-6px;right:-6px;background:white;color:#0284c7;
              border-radius:9999px;min-width:14px;height:14px;padding:0 3px;font-size:9px;
              font-weight:bold;font-family:Arial,sans-serif;display:flex;align-items:center;
              justify-content:center;box-shadow:0 1px 2px rgba(0,0,0,0.3);">${count}</div>`
          : '';
      const label = `Customer stop, ${count} ${count === 1 ? 'job' : 'jobs'}`;
      const html = `<div role="img" aria-label="${label}" style="position:relative;
          width:${size}px;height:${size}px;border-radius:6px;background:#0ea5e9;
          border:${borderWidth}px solid ${borderColor};box-shadow:${boxShadow};
          opacity:${dimmed ? 0.4 : 1};display:flex;align-items:center;justify-content:center;
          animation:fadeIn 0.3s ease-out;">${houseSvg}${badge}</div>`;

      const fanOffsetPx = fanIndex * 12;
      const icon = L.divIcon({
        html,
        className: 'manual-stop-marker customer-stop-marker',
        iconSize: L.point(size, size, true),
        iconAnchor: L.point(size / 2 - fanOffsetPx, size / 2, true)
      });
      manualStopIconCacheRef.current.set(key, icon);
      return icon;
    };

    // Calculate which dealers have manual stops and collect the stops for each dealer
    const dealerManualStopsMap = useMemo(() => {
      if (!stagedManualStops || stagedManualStops.length === 0)
        return new Map<string, typeof stagedManualStops>();

      const threshold = 0.0005; // ~50 meters
      const dealerStopsMap = new Map<string, typeof stagedManualStops>();

      dealerLocations.forEach(dealer => {
        if (!dealer.coordinates?.latitude || !dealer.coordinates?.longitude) return;

        // Customer stops are never folded into a dealer marker, however close.
        const matchingStops = stagedManualStops.filter(stop => {
          if (isCustomerStop(stop)) return false;
          const latDiff = Math.abs(dealer.coordinates.latitude - stop.dealer_latitude);
          const lngDiff = Math.abs(dealer.coordinates.longitude - stop.dealer_longitude);
          return latDiff < threshold && lngDiff < threshold;
        });

        if (matchingStops.length > 0) {
          dealerStopsMap.set(dealer.dealerId, matchingStops);
        }
      });

      return dealerStopsMap;
    }, [dealerLocations, stagedManualStops]);

    // Group manual stops by location - ONLY for stops NOT at existing dealer locations
    // Stops at dealer locations are shown in the combined dealer marker instead
    const groupedManualStops = useMemo(() => {
      if (!stagedManualStops || stagedManualStops.length === 0) return [];

      const threshold = 0.0005; // ~50 meters (same as dealer detection)

      // Filter out stops that are at dealer locations (these are shown in dealer marker).
      // Customer stops are grouped separately below, by household.
      const stopsNotAtDealers = stagedManualStops.filter(stop => {
        if (isCustomerStop(stop)) return false;
        const isAtDealer = dealerLocations.some(d => {
          if (!d.coordinates?.latitude || !d.coordinates?.longitude) return false;
          return (
            Math.abs(d.coordinates.latitude - stop.dealer_latitude) < threshold &&
            Math.abs(d.coordinates.longitude - stop.dealer_longitude) < threshold
          );
        });
        return !isAtDealer;
      });

      const groups: Array<{
        lat: number;
        lng: number;
        count: number;
        stops: typeof stagedManualStops;
        isCustomer?: boolean;
        fanIndex?: number;
      }> = [];

      stopsNotAtDealers.forEach(stop => {
        // Find existing group within threshold
        const existingGroup = groups.find(
          g =>
            Math.abs(g.lat - stop.dealer_latitude) < threshold &&
            Math.abs(g.lng - stop.dealer_longitude) < threshold
        );

        if (existingGroup) {
          existingGroup.count++;
          existingGroup.stops.push(stop);
        } else {
          groups.push({
            lat: stop.dealer_latitude,
            lng: stop.dealer_longitude,
            count: 1,
            stops: [stop]
          });
        }
      });

      // Customer stops: one marker per household (location key), never merged with
      // neighbours. Households at the same point (units in one building) fan out.
      const customerGroups = new Map<string, (typeof groups)[number]>();
      stagedManualStops.filter(isCustomerStop).forEach(stop => {
        const key = getStopLocationKey(stop);
        const existing = customerGroups.get(key);
        if (existing) {
          existing.count++;
          existing.stops.push(stop);
        } else {
          customerGroups.set(key, {
            lat: stop.dealer_latitude,
            lng: stop.dealer_longitude,
            count: 1,
            stops: [stop],
            isCustomer: true
          });
        }
      });
      const fanByPoint = new Map<string, number>();
      customerGroups.forEach(group => {
        const point = `${group.lat.toFixed(5)},${group.lng.toFixed(5)}`;
        const index = fanByPoint.get(point) ?? 0;
        fanByPoint.set(point, index + 1);
        groups.push({ ...group, fanIndex: index });
      });

      return groups;
    }, [stagedManualStops, dealerLocations]);

    // Track map bounds changes and trigger data loading
    useEffect(() => {
      if (!map || !onMapBoundsChange) return;

      let debounceTimer: ReturnType<typeof setTimeout> | null = null;
      let lastBounds: { north: number; south: number; east: number; west: number } | null = null;

      const handleMapMove = () => {
        try {
          // Ensure map is ready and has a container
          const mapLoaded = (map as { _loaded?: boolean })._loaded;
          if (!mapLoaded || !map.getContainer()) {
            return;
          }

          // Clear existing timer
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }

          // Debounce the bounds change to prevent excessive loading
          debounceTimer = setTimeout(() => {
            // Double-check map still exists before getting bounds
            if (!map || !map.getContainer()) {
              return;
            }

            try {
              const bounds = map.getBounds();
              const newBounds = {
                north: bounds.getNorth(),
                south: bounds.getSouth(),
                east: bounds.getEast(),
                west: bounds.getWest()
              };

              // Only trigger bounds change if the bounds have actually changed significantly
              if (
                !lastBounds ||
                Math.abs(newBounds.north - lastBounds.north) > 0.001 ||
                Math.abs(newBounds.south - lastBounds.south) > 0.001 ||
                Math.abs(newBounds.east - lastBounds.east) > 0.001 ||
                Math.abs(newBounds.west - lastBounds.west) > 0.001
              ) {
                lastBounds = newBounds;
                onMapBoundsChange(newBounds);
              }
            } catch {
              // Silently handle bounds calculation errors
            }
          }, 50); // Minimal debounce - just enough to batch rapid events
        } catch {
          // Silently handle map bounds errors
        }
      };

      // Wait for map to be ready before setting initial bounds
      const setInitialBounds = () => {
        const mapLoaded = (map as { _loaded?: boolean })._loaded;
        if (mapLoaded) {
          handleMapMove();
        } else {
          // Wait for map to load
          setTimeout(setInitialBounds, 100);
        }
      };

      // Set initial bounds when map is ready
      setInitialBounds();

      // Listen for map movement events
      map.on('moveend', handleMapMove);
      map.on('zoomend', handleMapMove);

      return () => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        map.off('moveend', handleMapMove);
        map.off('zoomend', handleMapMove);
      };
    }, [map, onMapBoundsChange]);

    // Always show the map, even when there are no dealer locations
    // This provides a better user experience when dragging to areas with no recalls

    return (
      <div style={{ height: '100%', width: '100%', position: 'relative' }}>
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          ref={mapInstance => {
            if (mapInstance && mapInstance !== mapRef.current) {
              mapRef.current = mapInstance;
              setMap(mapInstance);
            }
          }}
          // @ts-ignore - whenCreated may not be in types but works at runtime
          whenCreated={(mapInstance: L.Map) => {
            mapRef.current = mapInstance;
            setMap(mapInstance);
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />

          {/* Blue rings removed - manual stops at dealers now shown as badge on dealer marker */}

          {/* Dealer markers */}
          {dealerLocations.map(dealer => {
            if (!dealer.coordinates?.latitude || !dealer.coordinates?.longitude) {
              return null;
            }

            // Use recallCount (total recalls)
            const recallCount = dealer.recallCount || dealer.vehicleCount;

            // Get manual stops at this dealer location
            const manualStopsAtDealer = dealerManualStopsMap.get(dealer.dealerId) || [];
            const manualStopCount = manualStopsAtDealer.length;

            // Fold manual stops at this dealer into the technician match (only
            // byTechnician — progress fields stay recall-only so the ring is
            // unaffected). A dealer whose sole selected-tech work is a manual
            // stop should highlight rather than dim.
            const baseSummary = dealer.assignmentSummary;
            const manualStopTech = manualStopsByTechnician(manualStopsAtDealer);
            let summaryForVisuals = baseSummary;
            if (manualStopTech.size > 0 && baseSummary) {
              const byTechnician = mergeTechCounts(baseSummary.byTechnician, manualStopTech);
              summaryForVisuals = { ...baseSummary, byTechnician };
            }

            // Assignment encoding (ring %, done-state, color, dimming)
            const { pctHandled, fullyHandled, ringColor, dimmed, emphasized } =
              getDealerMarkerVisuals(summaryForVisuals, {
                colorByTechnician,
                selectedTechnicianIds,
                getTechnicianColor
              });

            return (
              <Marker
                key={dealer.dealerId}
                position={[dealer.coordinates.latitude, dealer.coordinates.longitude]}
                icon={createDealerIcon(recallCount, manualStopCount, {
                  pctHandled,
                  fullyHandled,
                  ringColor,
                  dimmed,
                  emphasized
                })}
                eventHandlers={{
                  click: () => {
                    if (onDealerClick) {
                      onDealerClick(dealer.dealerId);
                    }
                  }
                }}
              >
                <Tooltip direction='top' offset={[0, -10]} opacity={0.95}>
                  <div className='text-sm text-center px-2 py-1'>
                    <span className='font-bold'>{dealer.name || 'Dealer Location'}</span>
                    {/* Just-in-time detail so the legend stays secondary */}
                    <span className='block text-xs font-normal text-muted-foreground'>
                      {recallCount} {recallCount === 1 ? 'recall' : 'recalls'}
                      {dealer.assignmentSummary && dealer.assignmentSummary.total > 0 && (
                        <> • {Math.round(pctHandled)}% assigned</>
                      )}
                    </span>
                    {manualStopCount > 0 && (
                      <span className='block text-xs font-normal' style={{ color: '#0ea5e9' }}>
                        +{manualStopCount} {manualStopCount === 1 ? 'job' : 'jobs'}
                      </span>
                    )}
                  </div>
                </Tooltip>
                <Popup maxWidth={350} maxHeight={400}>
                  <div className='p-3 max-h-96 overflow-y-auto'>
                    <h3 className='font-bold text-lg mb-2 text-foreground'>{dealer.name}</h3>
                    <p className='mb-3 text-sm text-muted-foreground'>{dealer.address}</p>
                    <div className='flex items-center gap-2 mb-3 flex-wrap'>
                      <span
                        className={[
                          'px-2 py-1 rounded text-xs bg-muted text-muted-foreground',
                          'border border-border font-bold'
                        ].join(' ')}
                      >
                        {dealer.recallCount || dealer.vehicleCount} recalls
                      </span>
                      <span
                        className={[
                          'px-2 py-1 rounded text-xs bg-muted text-muted-foreground',
                          'border border-border'
                        ].join(' ')}
                      >
                        {dealer.vehicleCount} vehicles
                      </span>
                      {manualStopCount > 0 && (
                        <span
                          className='px-2 py-1 rounded text-xs font-medium'
                          style={{ backgroundColor: '#0ea5e9', color: 'white' }}
                        >
                          {manualStopCount} {manualStopCount === 1 ? 'job' : 'jobs'}
                        </span>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* Manual stop markers for locations WITHOUT existing dealers */}
          {/* Manual stops at dealer locations are shown as a badge on the dealer marker */}
          {groupedManualStops.map(group => {
            // Run the group through the same visual resolver dealer markers use,
            // with a synthetic summary carrying only the stops' technician counts
            // (progress fields are irrelevant — the manual-stop icon ignores them).
            const { ringColor, dimmed, emphasized } = getDealerMarkerVisuals(
              {
                total: 0,
                handled: 0,
                unassigned: 0,
                fullyHandled: false,
                byTechnician: manualStopsByTechnician(group.stops)
              },
              { colorByTechnician, selectedTechnicianIds, getTechnicianColor }
            );
            if (group.isCustomer && group.stops[0]) {
              return (
                <CustomerStopMarker
                  key={`customer-${getStopLocationKey(group.stops[0])}`}
                  stops={group.stops}
                  lat={group.lat}
                  lng={group.lng}
                  icon={createCustomerStopIcon(group.count, group.fanIndex ?? 0, {
                    ringColor,
                    dimmed,
                    emphasized
                  })}
                  {...(onManualStopClick && { onOpen: onManualStopClick })}
                />
              );
            }
            return (
              <Marker
                key={`manual-group-${group.lat}-${group.lng}`}
                position={[group.lat, group.lng]}
                icon={createManualStopIcon(group.count, false, { ringColor, dimmed, emphasized })}
                zIndexOffset={1000}
                eventHandlers={{
                  click: () => {
                    if (onManualStopClick && group.stops[0]) {
                      onManualStopClick(group.stops[0].id);
                    }
                  }
                }}
              >
                <Tooltip direction='top' offset={[0, -10]} opacity={0.95}>
                  <div className='font-bold text-sm text-center px-2 py-1'>
                    {group.stops[0]?.dealer_name}
                    <span className='block text-xs font-normal text-muted-foreground'>
                      {group.count} {group.count === 1 ? 'job' : 'jobs'}
                    </span>
                  </div>
                </Tooltip>
                <Popup maxWidth={350} maxHeight={400}>
                  <div className='p-3 max-h-96 overflow-y-auto'>
                    {(() => {
                      const first = group.stops[0];
                      if (!first) return null;
                      const addressParts = [
                        first.dealer_address,
                        first.dealer_city,
                        first.dealer_region
                      ].filter(Boolean);
                      const addressLine =
                        addressParts.join(', ') +
                        (first.dealer_postal_code ? ` ${first.dealer_postal_code}` : '');
                      const vehicleCount = new Set(group.stops.map(s => s.vin || s.id)).size;
                      return (
                        <>
                          <h3 className='font-bold text-lg mb-1 text-foreground'>
                            {first.dealer_name}
                          </h3>
                          <p className='text-sm text-muted-foreground mb-2'>{addressLine}</p>
                          <div className='flex items-center gap-2 mb-3 flex-wrap'>
                            <span
                              className='px-2 py-1 rounded text-xs font-medium'
                              style={{ backgroundColor: '#0ea5e9', color: 'white' }}
                            >
                              {group.count} {group.count === 1 ? 'job' : 'jobs'}
                            </span>
                            <span
                              className='px-2 py-1 rounded text-xs font-medium'
                              style={{ backgroundColor: '#0ea5e9', color: 'white' }}
                            >
                              {vehicleCount} {vehicleCount === 1 ? 'vehicle' : 'vehicles'}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Always re-render if flyToTarget changes
    if (prevProps.flyToTarget !== nextProps.flyToTarget) {
      return false;
    }

    // Re-render when the tenant's location arrives, so the map can move to it once.
    if (
      prevProps.initialCenter?.[0] !== nextProps.initialCenter?.[0] ||
      prevProps.initialCenter?.[1] !== nextProps.initialCenter?.[1]
    ) {
      return false;
    }

    // Re-render when assignment state changes — the length-based comparison
    // below would otherwise miss it (counts unchanged, only ring/done updates).
    if (prevProps.summaryVersion !== nextProps.summaryVersion) {
      return false;
    }

    // Re-render when technician visualization controls change
    if (
      prevProps.colorByTechnician !== nextProps.colorByTechnician ||
      !sameOrderStringArray(prevProps.selectedTechnicianIds, nextProps.selectedTechnicianIds)
    ) {
      return false;
    }

    // Re-render when the set of staged manual stops (or their kind / assignment) changes —
    // not just the count: switching the Business/Customer filter can keep the length equal.
    const stopSignature = (stops: WorkingMapProps['stagedManualStops']) =>
      (stops || [])
        .map(s => `${s.id}:${s.stop_kind ?? 'business'}:${s.technicianId ?? ''}`)
        .join(',');
    if (stopSignature(prevProps.stagedManualStops) !== stopSignature(nextProps.stagedManualStops)) {
      return false; // Re-render
    }

    // Re-render when any marker's content changes, not only when the dealer count
    // moves by a threshold: a pan can swap dealers at an equal count, and a refreshed
    // dataset can change recall counts without adding or removing a dealer.
    if (prevProps.dealerLocations === nextProps.dealerLocations) {
      return true; // Skip re-render
    }
    return (
      dealerLocationsSignature(prevProps.dealerLocations) ===
      dealerLocationsSignature(nextProps.dealerLocations)
    );
  }
);

export default WorkingMap;
